import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  attendanceService,
  scheduleService,
  announcementService,
} from "../../firebase/services";
import {
  ShieldCheck,
  CalendarCheck,
  FileText,
  Calendar,
  Bell,
  ArrowRight,
  User,
  Clock,
  Sparkles,
  AlertCircle,
  Award,
} from "lucide-react";

export const StudentDashboard = () => {
  const { userProfile, currentUser } = useAuth();
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [todaySchedule, setTodaySchedule] = useState(null);
  const [latestAnnouncement, setLatestAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const studentId = userProfile?.studentId;
        const [attList, schedList, annList] = await Promise.all([
          studentId && currentUser?.uid ? attendanceService.getStudentAttendance(currentUser.uid) : Promise.resolve([]),
          scheduleService.getSchedules(),
          announcementService.getAnnouncements(),
        ]);

        setAttendanceRecords(attList);

        if (schedList && schedList.length > 0) {
          const todayIso = new Date().toISOString().split("T")[0];
          const matched = schedList.find((s) => s.date === todayIso);
          setTodaySchedule(matched || schedList[0]);
        }

        if (annList && annList.length > 0) {
          setLatestAnnouncement(annList[0]);
        }
      } catch (err) {
        console.error("Dashboard data load error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [userProfile, currentUser?.uid]);

  const presentDays = attendanceRecords.filter((r) => r.status === "Present").length;
  const totalMarkedDays = attendanceRecords.length;
  const attendancePercentage =
    totalMarkedDays > 0 ? Math.round((presentDays / totalMarkedDays) * 100) : 0;

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Top Banner / Student Identity */}
      <div className="p-6 rounded-2xl bg-gradient-to-r from-card via-card to-emerald-950/20 border border-border shadow-sm flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center space-x-4">
          {userProfile?.photoUrl ? (
            <img
              src={userProfile.photoUrl}
              alt="Passport Photo"
              className="w-16 h-16 rounded-xl object-cover border-2 border-emerald-500/40 shadow-sm"
            />
          ) : (
            <div className="w-16 h-16 rounded-xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold text-xl">
              {userProfile?.fullName ? userProfile.fullName.charAt(0) : "S"}
            </div>
          )}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-foreground">
                Welcome, {userProfile?.fullName || currentUser?.email?.split("@")[0] || "Student"}
              </h1>
              <span className="px-2 py-0.5 rounded text-[10px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                Active Enrolled
              </span>
            </div>
            <p className="text-xs font-mono text-muted-foreground flex items-center gap-3 flex-wrap">
              <span>
                Student ID:{" "}
                <strong className="text-emerald-400">
                  {userProfile?.studentId || "KIAN-CYBER-PENDING"}
                </strong>
              </span>
              <span>•</span>
              <span>15 Days Free Cyber Security Masterclass</span>
              <span>•</span>
              <span>{userProfile?.college || "Bhilai Campus"}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/student/profile"
            data-testid="dash-view-profile-btn"
            className="px-4 py-2 rounded-lg border border-border bg-card hover:bg-muted text-xs font-mono font-medium transition-colors"
          >
            View ID Card
          </Link>
        </div>
      </div>

      {/* Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Attendance Percentage */}
        <div className="p-5 rounded-xl bg-card border border-border space-y-3">
          <div className="flex items-center justify-between text-muted-foreground text-xs font-mono">
            <span>Attendance Rate</span>
            <CalendarCheck className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="flex items-baseline space-x-2">
            <span className="text-3xl font-extrabold text-foreground font-mono">
              {totalMarkedDays > 0 ? `${attendancePercentage}%` : "0%"}
            </span>
            <span className="text-xs text-muted-foreground font-mono">
              ({presentDays}/{totalMarkedDays || 15} sessions)
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div
              className={`h-full ${
                attendancePercentage >= 85
                  ? "bg-emerald-500"
                  : attendancePercentage >= 50
                  ? "bg-amber-500"
                  : "bg-cyan-500"
              }`}
              style={{ width: `${Math.min(attendancePercentage, 100)}%` }}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            {attendancePercentage >= 85
              ? "✓ On track for Masterclass Certificate eligibility (≥85%)."
              : "Attend upcoming sessions to maintain certification eligibility."}
          </p>
        </div>

        {/* Masterclass Program Status */}
        <div className="p-5 rounded-xl bg-card border border-border space-y-3">
          <div className="flex items-center justify-between text-muted-foreground text-xs font-mono">
            <span>Program Status</span>
            <ShieldCheck className="w-4 h-4 text-cyan-400" />
          </div>
          <div className="space-y-1">
            <div className="text-base font-bold text-foreground">15 Days Cyber Masterclass</div>
            <div className="text-xs text-muted-foreground">Institute: Kian Technologies, Bhilai</div>
          </div>
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 text-[11px] font-mono">
            <Award className="w-3.5 h-3.5" />
            <span>Practical Lab Access Active</span>
          </div>
        </div>

        {/* Quick Material Access */}
        <div className="p-5 rounded-xl bg-card border border-border space-y-3">
          <div className="flex items-center justify-between text-muted-foreground text-xs font-mono">
            <span>Study Resources</span>
            <FileText className="w-4 h-4 text-emerald-400" />
          </div>
          <div className="space-y-1">
            <div className="text-base font-bold text-foreground">Daily Lab Notes & Tools</div>
            <div className="text-xs text-muted-foreground">PDFs and practical lab guides</div>
          </div>
          <Link
            to="/student/materials"
            className="text-xs font-mono text-emerald-400 hover:text-emerald-300 flex items-center gap-1 font-semibold"
          >
            <span>Open Materials Library</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {/* Middle Section: Today's Class & Latest Announcement */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Today's Schedule Card */}
        <div className="lg:col-span-7 bg-card border border-border rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div className="flex items-center space-x-2">
              <Clock className="w-4 h-4 text-cyan-400" />
              <h2 className="font-semibold text-sm">Scheduled Session / Topic</h2>
            </div>
            <Link
              to="/student/schedule"
              className="text-xs font-mono text-cyan-400 hover:underline"
            >
              Full Schedule →
            </Link>
          </div>

          {todaySchedule ? (
            <div className="p-4 rounded-lg bg-muted/40 border border-border space-y-3">
              <div className="flex items-center justify-between">
                <span className="px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-mono text-xs font-bold border border-cyan-500/20">
                  Day {todaySchedule.dayNumber}
                </span>
                <span className="text-xs font-mono text-muted-foreground">
                  {todaySchedule.date} • {todaySchedule.startTime} - {todaySchedule.endTime}
                </span>
              </div>
              <div>
                <h3 className="font-bold text-base text-foreground">{todaySchedule.topic}</h3>
                {todaySchedule.instructor && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Instructor: {todaySchedule.instructor}
                  </p>
                )}
              </div>
              {todaySchedule.description && (
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {todaySchedule.description}
                </p>
              )}
            </div>
          ) : (
            <div className="p-6 text-center text-xs text-muted-foreground font-mono">
              No class schedule published yet. Please check back soon.
            </div>
          )}
        </div>

        {/* Latest Announcement */}
        <div className="lg:col-span-5 bg-card border border-border rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between border-b border-border pb-3">
            <div className="flex items-center space-x-2">
              <Bell className="w-4 h-4 text-emerald-400" />
              <h2 className="font-semibold text-sm">Latest Announcement</h2>
            </div>
            <Link
              to="/student/announcements"
              className="text-xs font-mono text-emerald-400 hover:underline"
            >
              All →
            </Link>
          </div>

          {latestAnnouncement ? (
            <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 uppercase font-bold border border-emerald-500/20">
                  {latestAnnouncement.priority || "Announcement"}
                </span>
                <span className="text-[11px] font-mono text-muted-foreground">
                  {latestAnnouncement.createdAt?.toDate
                    ? latestAnnouncement.createdAt.toDate().toLocaleDateString()
                    : "Recent"}
                </span>
              </div>
              <h3 className="font-bold text-sm text-foreground">{latestAnnouncement.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3">
                {latestAnnouncement.content}
              </p>
            </div>
          ) : (
            <div className="p-6 text-center text-xs text-muted-foreground font-mono">
              No new announcements.
            </div>
          )}
        </div>
      </div>

      {/* Quick Links Grid */}
      <div className="p-5 rounded-xl bg-card border border-border space-y-4">
        <h2 className="font-semibold text-sm text-foreground">Quick Portal Navigation</h2>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Link
            to="/student/profile"
            className="p-3 rounded-lg bg-muted/40 hover:bg-muted border border-border text-center space-y-2 transition-colors"
          >
            <User className="w-5 h-5 mx-auto text-emerald-400" />
            <span className="block text-xs font-mono font-medium">My Profile</span>
          </Link>
          <Link
            to="/student/attendance"
            className="p-3 rounded-lg bg-muted/40 hover:bg-muted border border-border text-center space-y-2 transition-colors"
          >
            <CalendarCheck className="w-5 h-5 mx-auto text-cyan-400" />
            <span className="block text-xs font-mono font-medium">Attendance</span>
          </Link>
          <Link
            to="/student/materials"
            className="p-3 rounded-lg bg-muted/40 hover:bg-muted border border-border text-center space-y-2 transition-colors"
          >
            <FileText className="w-5 h-5 mx-auto text-emerald-400" />
            <span className="block text-xs font-mono font-medium">Study Material</span>
          </Link>
          <Link
            to="/student/schedule"
            className="p-3 rounded-lg bg-muted/40 hover:bg-muted border border-border text-center space-y-2 transition-colors"
          >
            <Calendar className="w-5 h-5 mx-auto text-cyan-400" />
            <span className="block text-xs font-mono font-medium">Schedule</span>
          </Link>
          <Link
            to="/student/announcements"
            className="p-3 rounded-lg bg-muted/40 hover:bg-muted border border-border text-center space-y-2 transition-colors col-span-2 sm:col-span-1"
          >
            <Bell className="w-5 h-5 mx-auto text-emerald-400" />
            <span className="block text-xs font-mono font-medium">Announcements</span>
          </Link>
        </div>
      </div>
    </div>
  );
};
